const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/modules/CIS4m/components/CIS4mNumberField.tsx';
import * as React from 'react';
import { forwardRef } from 'react';
import { useCIS4mValidation } from '../hooks';
import { CIS4mValueComponentProps } from '../types';
import { InputAdornment, TextField, TextFieldProps } from '@mui/material';
import textMap from './translations';
import { useIntl } from 'react-intl';
import { TextInput, useOptionalText, useText } from '../../../utils/intlUtils';
import { useCIS4mContext } from '../context';

interface Props extends CIS4mValueComponentProps {
    label: TextInput;
    unit?: string;
    required?: boolean;
    errorMessageWhenRequired?: TextInput;
    minVal?: number;
    maxVal?: number;
    maxLength?: number;
    initialValue?: number;
    fullWidth?: boolean;
    fractionDigits?: number;
    helperText?: TextInput;
    noStyle?: boolean;
}

export const CIS4mNumberField = forwardRef((props: Props, ref: any) => {
    const {
        disabled = false,
        errorMessageWhenRequired,
        fractionDigits,
        fullWidth,
        hidden = false,
        initialValue,
        maxVal,
        minVal,
        noStyle,
        path,
        readonly = false,
        required = false,
        unit,
    } = props;
    const { formatMessage } = useIntl();
    const label = useText(props.label);
    const helperText = useOptionalText(props.helperText);

    const { variant } = useCIS4mContext().style;

    const [value, setValue, errorMessage] = useCIS4mValidation(ref, {
        checkValidity: () => {
            if (minVal !== undefined && parseInt(value, 10) < minVal) {
                return formatMessage(textMap.mustBeAtLeast, { min: minVal });
            }
            if (maxVal !== undefined && parseInt(value, 10) > maxVal) {
                return formatMessage(textMap.mustBeLeqThan, { max: maxVal });
            }
        },
        disabled,
        errorMessageWhenRequired,
        hidden,
        initialValue,
        path,
        required,
    });

    const sxTextField: TextFieldProps['sx'] = {
        mt: 0.5,
        mb: 0.5,
        mr: fullWidth ? 0 : 2,
    };

    return hidden ? null : (
        <TextField
            label={label}
            fullWidth={fullWidth}
            variant={variant}
            size={'small'}
            value={value || ''}
            onChange={(event: any) => {
                const newValue = event.target.value;
                // Allow empty string, minus sign (for typing negative numbers), or valid numbers
                if (newValue === '' || newValue === '-' || !Number.isNaN(Number(newValue))) {
                    setValue(newValue);
                }
            }}
            onBlur={(event: any) => {
                let newValue = event.target.value;
                if (fractionDigits !== undefined && fractionDigits > 0) {
                    newValue = parseFloat(newValue).toFixed(fractionDigits).toString();
                }
                setValue(!Number.isNaN(Number(newValue)) ? newValue : undefined);
            }}
            slotProps={{
                input: {
                    readOnly: readonly,
                    inputMode: 'decimal',
                    lang: 'de-CH',
                    endAdornment: <InputAdornment position="end">{unit}</InputAdornment>,
                    inputProps: {
                        maxLength: props.maxLength,
                    },
                },
            }}
            color={!!errorMessage ? 'error' : 'primary'}
            required={required}
            disabled={disabled}
            hidden={hidden}
            error={!!errorMessage}
            helperText={errorMessage || helperText} // errorMessage will have more priority than helperText when there is one
            sx={!noStyle ? sxTextField : undefined}
        />
    );
});

CIS4mNumberField.displayName = 'CIS4mNumberField';
